import { useEffect } from "react";

/**
 * Drives the browser tab's favicon + title from the resolved brand
 * cascade. The POS web bundle ships with the default Vite icon and
 * the literal title "POS" baked into ``index.html`` - that's what
 * shows up before any anchor is resolved. Once we know which
 * partner / org the operator is signing into, we want the tab to
 * read as that brand: their logo on the favicon, their product name
 * in the title.
 *
 * Cascade priority (caller has already merged via the API):
 *   ``favicon_url``  → preferred; brand can ship a transparent
 *                      square specifically sized for tabs / PWA.
 *   ``logo_url``     → fallback; the same image that runs in the
 *                      header is fine for the favicon if no
 *                      favicon-specific image is configured.
 *   none → default Vite icon stays in place.
 *
 * Title cascade:
 *   ``product_name`` → preferred, e.g. "Honda POS".
 *   ``partner.name`` style hint not surfaced through the merged
 *                      brand object - caller can pass an explicit
 *                      ``fallbackTitle`` to override the literal "POS".
 *
 * Mounted at the top of every shell that has a brand in scope
 * (LoginPage before sign-in, AppShell after). Both call sites can
 * mount it concurrently - the more-specific instance unmounts last
 * and restores the defaults on the way out, so the tab never gets
 * stuck on a stale logo.
 */

interface BrandFaviconProps {
  readonly brand: Record<string, unknown>;
  /** Override the literal "POS" title fallback when ``product_name``
   *  isn't on the brand. e.g. the login page passes the anchor's
   *  display name so the tab reads "Honda Group" pre-sign-in. */
  readonly fallbackTitle?: string;
}

function pickString(brand: Record<string, unknown>, key: string): string | null {
  const v = brand[key];
  return typeof v === "string" && v.trim().length > 0 ? v.trim() : null;
}

export function BrandFavicon({ brand, fallbackTitle }: BrandFaviconProps) {
  useEffect(() => {
    const faviconHref = pickString(brand, "favicon_url") ?? pickString(brand, "logo_url");
    const productName = pickString(brand, "product_name") ?? fallbackTitle ?? null;

    // Track every node we mutate or insert so unmount restores
    // exactly what we found. Multiple BrandFavicon instances stack
    // cleanly: each one snapshots the pre-mount state and rolls
    // back on unmount, so a deep route's brand wins until you
    // navigate away.
    const previousTitle = document.title;
    const head = document.head;
    let restoreIcon: (() => void) | null = null;

    if (faviconHref) {
      const existing = head.querySelectorAll<HTMLLinkElement>('link[rel="icon"]');
      // Snapshot before mutating. We replace the FIRST icon link's
      // href and remove the rest temporarily so the browser doesn't
      // race-pick the original.
      const snapshots: Array<{ node: HTMLLinkElement; href: string; type: string }> = [];
      existing.forEach((node) => {
        snapshots.push({ node, href: node.href, type: node.type });
      });
      // Remove all but the first; mutate the first to point at the
      // brand favicon. Browsers re-fetch on href change.
      const first = existing[0];
      if (first) {
        first.href = faviconHref;
        // Type sniff: vite ships svg, brand assets usually png/jpg -
        // setting type="" lets the browser sniff from the URL.
        first.type = "";
      } else {
        const link = document.createElement("link");
        link.rel = "icon";
        link.href = faviconHref;
        head.appendChild(link);
        snapshots.push({ node: link, href: "", type: "" });
      }
      restoreIcon = () => {
        for (const { node, href, type } of snapshots) {
          if (href) {
            node.href = href;
            node.type = type;
          } else {
            node.remove();
          }
        }
      };
    }

    if (productName) document.title = productName;

    return () => {
      if (restoreIcon) restoreIcon();
      document.title = previousTitle;
    };
  }, [brand, fallbackTitle]);

  return null;
}
